/**
 * <PatientChartSidePanelHost>
 *
 * Shared host that owns the `panelState` slot, the `openPanel` / `closePanel`
 * / `replacePanelContent` / `emitPanelEvent` callbacks, the
 * full-width-toggle state, the `<PatientSidePanelContext.Provider>` wrapper,
 * AND the floating `<PatientSidePanel>` overlay mount.
 *
 * Both `<PatientPage>` (/patients/:id) and `<CallPage>` (/call/:encounterId)
 * mount the same `<PatientChartActionBar>`, and that bar's "New Encounter →
 * Virtual Visit" button calls `usePatientSidePanel().openPanel(...)`. Before
 * this wrapper existed, `<CallPage>` mounted the action bar WITHOUT a
 * surrounding `<PatientSidePanelContext.Provider>`, so the hook fell back to
 * the no-op stub at `PatientSidePanelContext.tsx:36-38` and the button was
 * inert.
 *
 * Behavior forks
 *  - Default (no `onVirtualVisitSelect`): the Virtual Visit click opens the
 *    `newEncounterSoap` panel via the standard `openPanel` flow. `<PatientPage>`
 *    detects `panelState.panelType === 'newEncounterSoap'` and renders the
 *    Progress Note edit form inline as the chart's main content.
 *  - With `onVirtualVisitSelect` provided: the Virtual Visit click invokes
 *    the callback with the active encounter id INSTEAD of opening the panel.
 *    `<CallPage>` uses this branch to start Zoom on the encounter that's
 *    already in the URL (we're already at `/call/:encounterId` — we just
 *    haven't joined yet).
 *
 * The Care Coordinator panel always routes through the default `openPanel`
 * path so behavior on /patients/:id is unchanged.
 */
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CareCoordinatorNote } from './CareCoordinatorNote.tsx'
import { PatientSidePanel, PATIENT_SIDE_PANEL_WIDTH } from './PatientSidePanel.tsx'
import { useZoomPanelWidthReservation } from '@/components/scribe/useZoomPanelWidthReservation'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'
import {
  ChartRefreshTarget,
  PatientPanelState,
  PatientPanelType,
  PatientSidePanelContext,
} from './PatientSidePanelContext.tsx'
import type { Patient } from '@medplum/fhirtypes'

type PatientChartSidePanelHostProps = {
  /** Patient whose chart we're rendering. Used to back the `careCoordinator` form. */
  patient: Patient
  /**
   * Optional encounter id for the current route — populated on /call/:encounterId
   * so the `onVirtualVisitSelect` override can fire against the active encounter.
   * Ignored when the override is not provided.
   */
  activeEncounterId?: string
  /**
   * Optional override for the "New Encounter → Virtual Visit" branch. When
   * provided AND `activeEncounterId` is set, the host invokes this callback
   * instead of dispatching `panelType: 'newEncounterSoap'` through the panel
   * state. Used by `<CallPage>` to start Zoom on the existing encounter.
   *
   * When NOT provided (the /patients/:id default), the Virtual Visit click
   * follows the normal panel-open path which `<PatientPage>` then renders as
   * the inline new-encounter edit form.
   */
  onVirtualVisitSelect?: (encounterId: string) => void
  children: ReactNode
}

export function PatientChartSidePanelHost({
  patient,
  activeEncounterId,
  onVirtualVisitSelect,
  children,
}: PatientChartSidePanelHostProps) {
  const queryClient = useQueryClient()
  const [panelState, setPanelState] = useState<PatientPanelState>({ open: false })
  const [isPanelFullWidth, setIsPanelFullWidth] = useState(false)
  const [panelEventVersions, setPanelEventVersions] = useState<Record<string, number>>({})
  const [refreshTarget, setRefreshTarget] = useState<ChartRefreshTarget>()

  const openPanel = useCallback(
    (panel: { panelType: PatientPanelType; payload?: Record<string, unknown> }) => {
      // Fork: on /call/:encounterId, the doctor clicking "New Encounter →
      // Virtual Visit" should START Zoom for the encounter already in the URL
      // rather than spawn a sibling new-encounter flow. The host owns this
      // decision via the optional callback so PatientChartActionBar stays
      // route-agnostic (it just calls openPanel and lets the host decide).
      if (panel.panelType === 'newEncounterSoap' && onVirtualVisitSelect && activeEncounterId) {
        onVirtualVisitSelect(activeEncounterId)
        return
      }
      setPanelState({ open: true, panelType: panel.panelType, payload: panel.payload })
      setIsPanelFullWidth(false)
    },
    [activeEncounterId, onVirtualVisitSelect],
  )

  const closePanel = useCallback(() => {
    setPanelState({ open: false })
    setIsPanelFullWidth(false)
  }, [])

  const replacePanelContent = useCallback(
    (panel: { panelType: PatientPanelType; payload?: Record<string, unknown> }) => {
      setPanelState({ open: true, panelType: panel.panelType, payload: panel.payload })
      setIsPanelFullWidth(false)
    },
    [],
  )

  const emitPanelEvent = useCallback(
    (eventName: string, target?: ChartRefreshTarget) => {
      setPanelEventVersions((prev) => ({
        ...prev,
        [eventName]: (prev[eventName] || 0) + 1,
      }))
      setRefreshTarget(target)
      if (patient.id) {
        void queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patient.id) })
      }
    },
    [patient.id, queryClient],
  )

  const setPanelFullWidth = useCallback((next: boolean) => {
    setIsPanelFullWidth(next)
  }, [])

  const clearRefreshTarget = useCallback(() => {
    setRefreshTarget(undefined)
  }, [])

  const panelContextValue = useMemo(
    () => ({
      panelState,
      openPanel,
      closePanel,
      replacePanelContent,
      emitPanelEvent,
      panelEventVersions,
      refreshTarget,
      clearRefreshTarget,
      isPanelFullWidth,
      setPanelFullWidth,
    }),
    [
      clearRefreshTarget,
      closePanel,
      emitPanelEvent,
      isPanelFullWidth,
      openPanel,
      panelEventVersions,
      panelState,
      refreshTarget,
      replacePanelContent,
      setPanelFullWidth,
    ],
  )

  // The overlay panel only ever hosts the `careCoordinator` flow today —
  // `newEncounterSoap` renders inline in `<PatientPage>` as the main content
  // (see comment at PatientPage.tsx:113-121). The overlay is therefore gated
  // strictly on the careCoordinator panel type. On /call/:encounterId no
  // overlay is expected (CallPage doesn't open Care Coordinator), but mounting
  // the panel conditionally on `careCoordinatorOpen` makes it a no-op there
  // (the panel returns null when `open=false`) so a future surface that needs
  // it Just Works.
  const careCoordinatorOpen = panelState.open && panelState.panelType === 'careCoordinator'

  // PD-1164 — reserve the panel's width via the shared `--zoom-panel-width` var
  // so the chart shrinks (clip edge at the panel's left edge) instead of the
  // panel overlapping it. Fullscreen reserves nothing (it covers the chart);
  // released when the panel closes.
  useZoomPanelWidthReservation(
    careCoordinatorOpen && !isPanelFullWidth ? PATIENT_SIDE_PANEL_WIDTH : null,
  )

  return (
    <PatientSidePanelContext.Provider value={panelContextValue}>
      {children}
      <PatientSidePanel
        open={careCoordinatorOpen}
        fullWidth={isPanelFullWidth}
        title="Care Coordinator Note"
        onClose={closePanel}
        onToggleFullWidth={() => setIsPanelFullWidth((prev) => !prev)}
      >
        {careCoordinatorOpen ? (
          <CareCoordinatorNote
            patient={patient}
            onSubmitted={(reference) => {
              emitPanelEvent(
                'progressNotes:refresh',
                reference
                  ? { kind: 'reference', reference }
                  : { kind: 'newestInTab', dataKey: 'progress-notes' },
              )
              closePanel()
            }}
            onCancel={closePanel}
          />
        ) : null}
      </PatientSidePanel>
    </PatientSidePanelContext.Provider>
  )
}
